import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { selectForwardAccumulatorProperty } from '@/store/forwardAccumulator/selectors.ts';
import { type AccumulatorPriceType, POSSIBLE_PRICE_TYPE } from '@/store/forwardAccumulator/state.ts';
import { useAppSelector } from '@/store/hooks.ts';

export const PremiumPriceType = () => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const premiumPriceType = useAppSelector((state) =>
    selectForwardAccumulatorProperty(state, tileId, 'premiumPriceType'),
  );
  const premiumTypeCurrencyIndex = useAppSelector((state) =>
    selectForwardAccumulatorProperty(state, tileId, 'premiumTypeCurrencyIndex'),
  );

  const currencyPair = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'currencyPair'));
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : ['', ''];

  const handlePremiumPriceTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      sgwtWebAnalytics?.trackEvent(
        'Forward Accumulator',
        'Premium price type changed',
        isDefined(event.target.value) ? event.target.value : 'cleared',
      );

      check({
        premiumPriceType: event.target.value as AccumulatorPriceType,
      });
    },
    [check, sgwtWebAnalytics],
  );

  const handlePremiumTypeCurrencyChange = useCallback(() => {
    check({
      premiumTypeCurrencyIndex: premiumTypeCurrencyIndex === 1 ? 2 : 1,
    });
  }, [check, premiumTypeCurrencyIndex]);

  return (
    <FormField formattedMessageId="targetAccumulator.form.priceType">
      <div className="d-flex">
        <Form.Select
          value={premiumPriceType}
          onChange={handlePremiumPriceTypeChange}
          size="sm"
          data-e2e="price-type-selector"
        >
          {POSSIBLE_PRICE_TYPE.map((priceType) => (
            <option key={priceType} value={priceType}>
              <FormattedMessage id={`targetAccumulator.form.priceType.${priceType}`} />
            </option>
          ))}
        </Form.Select>
        <button
          type="button"
          data-e2e="premium-type-currency"
          onClick={() => {
            handlePremiumTypeCurrencyChange();
          }}
          className={`${premiumTypeCurrencyIndex === 2 ? 'btn-warning' : ''} btn btn-discreet-primary btn-sm`}
        >
          {premiumTypeCurrencyIndex === 1 ? currency1 : currency2}
        </button>
      </div>
    </FormField>
  );
};
